import { ArrowPathIcon, PauseCircleIcon, PlayCircleIcon } from "@heroicons/react/24/outline";
import {
  PlusCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  TruckIcon,
  ArrowRightStartOnRectangleIcon,
  UserIcon,
  DocumentPlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CalendarDaysIcon,
  SunIcon,
} from "@heroicons/react/24/solid";
import {
  BriefcaseIcon,
  CheckCheckIcon,
  DollarSign,
  FileCheck,
  FileCog,
  Rocket,
  ShieldCheck,
} from "lucide-react";

const ICON_SIZE = 16;

export const STATUS_CONFIG = {
  "grn created": {
    label: "GRN Created",
    icon: <PlusCircleIcon width={18} color="#1d4ed8" />,
    color: "#dbeafe",
  },

  "grn approved": {
    label: "GRN Approved",
    icon: <CheckCircleIcon width={18} color="#16a34a" />,
    color: "#dcfce7",
  },

  approve: {
    label: "Approved",
    icon: <ShieldCheckIcon width={18} color="#15803d" />,
    color: "#bbf7d0",
  },
  cancelled: {
    label: "Cancelled",
    icon: <XCircleIcon width={18} color="#b91c1c" />,
    color: "#fee2e2",
  },
  "entrysheet created": {
    label: "Entry Sheet Created",
    icon: <DocumentPlusIcon width={18} color="#059669" />, // Emerald-600
    color: "#d1fae5",
  },

  "in release": {
    label: "In Release",
    icon: <ClockIcon width={18} color="#d97706" />,
    color: "#fef3c7",
  },

  "qtn received": {
    label: "QTN Received",
    icon: <CheckCheckIcon size={16} color="#15803d" strokeWidth={2.3} />,
    color: "#dbeafe",
  },

  acknowledged: {
    label: "Acknowledged",
    icon: <CheckCircleIcon width={18} color="#16a34a" />,
    color: "#dcfce7",
  },

  "asn created": {
    label: "ASN Created",
    icon: <PlusCircleIcon width={18} color="#2563eb" />,
    color: "#dbeafe",
  },
  "invoice created": {
    label: "Invoice Created",
    icon: <DocumentTextIcon width={18} color="#2563eb" />,
    color: "#dbeafe",
  },
  "invoice approved": {
    label: "Invoice Approved",
    icon: <CheckBadgeIcon width={18} color="#2563eb" />,
    color: "#dbeafe",
  },
  "in transit": {
    label: "In Transit",
    icon: <TruckIcon width={20} height={20} color="#1d4ed8" />,
    color: "#dbeafe",
  },
  "gatepass created": {
    label: "Gatepass Created",
    icon: <ArrowRightStartOnRectangleIcon width={18} color="#2563eb" />,
    color: "#dbeafe",
  },
  published: {
    label: "Published",
    icon: <Rocket size={16} color="#1d4ed8" strokeWidth={2.3} />,
    color: "#dbeafe",
  },
  "rfq created": {
    label: "RFQ Created",
    icon: <FileCheck size={16} color="#854d0e" strokeWidth={2.3} />,
    color: "#dbeafe",
  },
  "tech approved": {
    label: "Tech Approved",
    icon: <ShieldCheck size={16} color="#0f766e" strokeWidth={2.3} />,
    color: "#dbeafe",
  },
  "technical open": {
    label: "Technical Open",
    icon: <FileCog size={16} color="#1d9230" strokeWidth={2.3} />,
    color: "#dbeafe",
  },
  "technical approved": {
    label: "Technical Approved",
    icon: <ShieldCheck size={16} color="#0f766e" strokeWidth={2.3} />,
    color: "#dbeafe",
  },
  invited: {
    label: "Invited",
    icon: <FileCog size={16} color="#1d9230" strokeWidth={2.3} />,
    color: "#dbeafe",
  },
  "non invited": {
    label: "Non Invited",
    icon: <UserIcon width={16} height={16} color="#4b5563" />,
    color: "#dbeafe",
  },
  deactivated: {
    label: "Deactivated",
    icon: <XCircleIcon width={16} height={16} color="#dc2626" />,
    color: "#dbeafe",
  },
  "in registration": {
    label: "In Registration",
    icon: <ClockIcon width={ICON_SIZE} height={ICON_SIZE} color="#d97706" />,
    color: "#dbeafe",
  },
  "commercial open": {
    label: "Commercial Open",
    icon: <DollarSign width={ICON_SIZE} height={ICON_SIZE} color="#1d9230" />,
    color: "#dbeafe",
  },
  "commercial approved": {
    label: "Commercial Approved",
    icon: <DollarSign width={ICON_SIZE} height={ICON_SIZE} color="#1d9230" />,
    color: "#dbeafe",
  },

  "pr approved": {
    label: "PR Approved",
    icon: (
      <i
        className="bi bi-check-lg"
        style={{ fontSize: 16, color: "#15803d" }}
      ></i>
    ),
    color: "#dbeafe",
  },
  registered: {
    label: "Registered",
    icon: (
      <i
        className="bi bi-check-lg"
        style={{ fontSize: 16, color: "#15803d" }}
      ></i>
    ),
    color: "#dbeafe",
  },
  "pr created": {
    label: "PR Created",
    icon: <FileCheck size={16} color="#854d0e" strokeWidth={2.3} />,
    color: "#dbeafe",
  },
  new: {
    label: "New",
    icon: <PlusCircleIcon width={18} color="#059669" />, // emerald-600
    color: "#d1fae5", // emerald-100
  },
  submitted: {
    label: "Submitted",
    icon: <CheckCircleIcon width={18} color="#4f46e5" />, // indigo-600
    color: "#e0e7ff",
  },
  "kyc updated": {
    label: "Kyc Updated",
    icon: <ShieldCheckIcon width={18} color="#059669" />, // indigo-600
    color: "#e0e7ff",
  },
  "pending with l1": {
    label: "Pending with L1",
    icon: <ClockIcon width={18} color="#d97706" />,
    color: "#e0e7ff",
  },
  approved: {
    label: "approved",
    icon: <CheckCircleIcon width={18} color="#059669" />,
    color: "#e0e7ff",
  },
  "tender created": {
    label: "Tender Created",
    icon: <DocumentPlusIcon width={15} color="#4f46e5" />,
    color: "#e0e7ff",
  },
  job: {
    label: "Job",
    icon: <BriefcaseIcon width={15} color="#4f46e5" />,
    color: "#e0e7ff",
  },
  quotation: {
    label: "Quotation",
    icon: <DocumentPlusIcon width={15} color="#4f46e5" />,
    color: "#e0e7ff",
  },
  "estimation draft": {
    label: "Estimation Draft",
    icon: <PencilSquareIcon width={15} color="#6b7280" />,
    color: "#f3f4f6",
  },
  "estimation pending": {
    label: "Estimation Pending",
    icon: <ClockIcon width={15} color="#f59e0b" />,
    color: "#fef3c7",
  },
  created: {
    label: "Created",
    icon: <PlusCircleIcon width={18} color="#059669" />,
    color: "#d1fae5",
  },

  deleted: {
    label: "Deleted",
    icon: <TrashIcon width={18} color="#dc2626" />,
    color: "#fee2e2",
  },

  draft: {
    label: "Draft",
    icon: <PencilSquareIcon width={18} color="#dc2626" />,
    color: "#fee2e2",
  },
  "offer Letter": {
    label: "Offer Letter",
    icon: <DocumentPlusIcon width={15} color="#4f46e5" />,
    color: "#e0e7ff",
  },
  started: {
    label: "Started",
    icon: <PlayCircleIcon width={15} color="#2563eb" />,
    color: "#dbeafe",
  },

  "On Hold": {
    label: "On Hold",
    icon: <PauseCircleIcon width={15} color="#f59e0b" />,
    color: "#fef3c7",
  },

  completed: {
    label: "Completed",
    icon: <CheckCircleIcon width={15} color="#16a34a" />,
    color: "#dcfce7",
  },

  pending: {
    label: "Pending",
    icon: <ClockIcon width={15} color="#6b7280" />,
    color: "#f3f4f6",
  },

  "In Progress": {
    label: "In Progress",
    icon: <ArrowPathIcon width={15} color="#7c3aed" />,
    color: "#ede9fe",
  },

  CANCELLED: {
    label: "Cancelled",
    icon: <XCircleIcon width={15} color="#dc2626" />,
    color: "#fee2e2",
  },

  EDITED: {
    label: "Edited",
    icon: <PencilSquareIcon width={15} color="#4f46e5" />,
    color: "#e0e7ff",
  },
   Present: {
    label: "Present",
    icon: <CheckCircleIcon width={20} height={20} color="#16a34a" />,
    color: "#dcfce7",
  },

  Absent: {
    label: "Absent",
    icon: <XCircleIcon width={20} height={20} color="#dc2626" />,
    color: "#fee2e2",
  },

  Late: {
    label: "Late",
    icon: <ClockIcon width={20} height={20} color="#d97706" />,
    color: "#fef3c7",
  },

  Leave: {
    label: "On Leave",
    icon: <CalendarDaysIcon width={20} height={20} color="#7c3aed" />,
    color: "#ede9fe",
  },

  Holiday: {
    label: "Holiday",
    icon: <SunIcon width={20} height={20} color="#2563eb" />,
    color: "#dbeafe",
  },
};
